import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type ColumnDef } from "@tanstack/react-table";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { MoreHorizontal, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";

import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { DataTable } from "@/components/data-table/DataTable";
import { PageHeader } from "@/components/PageHeader";
import { useDepartments } from "@/hooks/useDepartments";

export const Route = createFileRoute("/_app/users/")({ component: UsersPage });

interface UserRow {
  id: number;
  email: string;
  name: string;
  status: "active" | "suspended" | "invited";
  last_login_at: number | null;
  created_at: number;
  roles: string | null; // GROUP_CONCAT
  dept_scope: string[]; // department_head scope (slugs)
}

/** The role whose assignment carries a department scope (see krce-api routes/users.ts). */
const SCOPED_ROLE = "department_head";

interface RoleRow { id: number; slug: string; name: string }

function UsersPage() {
  const { user: me, can } = useAuth();
  const qc = useQueryClient();
  const [editing, setEditing] = useState<UserRow | null>(null);
  const [creating, setCreating] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["users", "list"],
    queryFn: () => api.get<{ items: UserRow[] }>("/admin/users"),
  });

  const remove = useMutation({
    mutationFn: (id: number) => api.del(`/admin/users/${id}`),
    onSuccess: () => { toast.success("User removed"); qc.invalidateQueries({ queryKey: ["users"] }); },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Delete failed"),
  });

  const columns: ColumnDef<UserRow, unknown>[] = [
    {
      accessorKey: "name", header: "Name",
      cell: ({ row }) => (
        <div>
          <p className="font-medium">{row.original.name}</p>
          <p className="text-xs text-muted-foreground">{row.original.email}</p>
        </div>
      ),
    },
    {
      id: "roles", header: "Roles",
      cell: ({ row }) => (
        <div className="flex flex-wrap gap-1">
          {(row.original.roles ?? "").split(",").filter(Boolean).map((r) => (
            <Badge key={r} variant="secondary">
              {r}
              {r === SCOPED_ROLE && (
                <span className="ml-1 font-mono text-[10px] uppercase text-muted-foreground">
                  {row.original.dept_scope.length ? row.original.dept_scope.join(", ") : "no dept"}
                </span>
              )}
            </Badge>
          ))}
        </div>
      ),
    },
    {
      accessorKey: "status", header: "Status",
      cell: ({ row }) => <Badge variant={row.original.status === "active" ? "success" : row.original.status === "invited" ? "warning" : "outline"}>{row.original.status}</Badge>,
      filterFn: (r, _i, v) => r.getValue("status") === v,
    },
    {
      accessorKey: "last_login_at", header: "Last login",
      cell: ({ row }) => row.original.last_login_at
        ? format(row.original.last_login_at * 1000, "MMM d, h:mma")
        : <span className="text-muted-foreground">—</span>,
    },
    {
      id: "actions", header: "",
      cell: ({ row }) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8"><MoreHorizontal className="h-4 w-4" /></Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-44">
            <DropdownMenuItem onClick={() => setEditing(row.original)}>Edit</DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              disabled={!can("users", "delete") || row.original.id === me?.id}
              onClick={() => { if (confirm(`Delete ${row.original.email}?`)) remove.mutate(row.original.id); }}
              className="text-destructive focus:text-destructive"
            ><Trash2 className="h-3.5 w-3.5" /> Delete</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  return (
    <div className="space-y-2">
      <PageHeader
        title="Users"
        description="Admin team members. Roles control which CMS sections they can edit."
        actions={can("users", "create") && (
          <Button onClick={() => setCreating(true)}><Plus className="h-4 w-4" /> New user</Button>
        )}
      />
      <DataTable
        columns={columns}
        data={data?.items ?? []}
        globalSearch
        globalSearchPlaceholder="Search by name, email, or role…"
        filters={[{ id: "status", label: "Status", options: [
          { value: "active", label: "Active" }, { value: "suspended", label: "Suspended" }, { value: "invited", label: "Invited" },
        ]}]}
        emptyMessage={isLoading ? "Loading…" : "No users yet."}
      />

      <UserDialog
        open={creating || !!editing}
        onOpenChange={(b) => { if (!b) { setCreating(false); setEditing(null); } }}
        user={editing}
      />
    </div>
  );
}

/** A department head with no department can edit nothing — refuse to save one. */
const scopeRequired = (v: { roles: string[]; dept_scope: string[] }) =>
  !v.roles.includes(SCOPED_ROLE) || v.dept_scope.length > 0;
const scopeIssue = { message: "Pick at least one department for a Department Head", path: ["dept_scope"] };

const createSchema = z.object({
  email: z.string().email(),
  name: z.string().min(2).max(120),
  password: z.string().min(10).max(256),
  roles: z.array(z.string()).min(1, "Pick at least one role"),
  dept_scope: z.array(z.string()),
}).refine(scopeRequired, scopeIssue);
const updateSchema = z.object({
  name: z.string().min(2).max(120),
  status: z.enum(["active", "suspended", "invited"]),
  roles: z.array(z.string()),
  dept_scope: z.array(z.string()),
  password: z.string().min(10, "Password must be at least 10 characters").max(256).optional().or(z.literal("")),
}).refine(scopeRequired, scopeIssue);

function UserDialog({
  open, onOpenChange, user,
}: {
  open: boolean;
  onOpenChange: (b: boolean) => void;
  user: UserRow | null;
}) {
  const isEdit = !!user;
  const qc = useQueryClient();
  const { data: rolesData } = useQuery({
    queryKey: ["roles", "list"],
    queryFn: () => api.get<{ items: (RoleRow & { is_system: number })[] }>("/admin/roles"),
    enabled: open,
  });

  // Two separate forms by mode (different schema)
  type CreateValues = z.infer<typeof createSchema>;
  type UpdateValues = z.infer<typeof updateSchema>;

  const createForm = useForm<CreateValues>({
    resolver: zodResolver(createSchema),
    values: { email: "", name: "", password: "", roles: [], dept_scope: [] },
  });
  const updateForm = useForm<UpdateValues>({
    resolver: zodResolver(updateSchema),
    values: user
      ? { name: user.name, status: user.status, roles: (user.roles ?? "").split(",").filter(Boolean), dept_scope: user.dept_scope ?? [], password: "" }
      : undefined,
  });

  const createMut = useMutation({
    mutationFn: (v: CreateValues) =>
      api.post("/admin/users", { ...v, dept_scope: v.roles.includes(SCOPED_ROLE) ? v.dept_scope : [] }),
    onSuccess: () => {
      toast.success("User created");
      qc.invalidateQueries({ queryKey: ["users"] });
      createForm.reset();
      onOpenChange(false);
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Create failed"),
  });
  const updateMut = useMutation({
    mutationFn: (v: UpdateValues) => {
      const body: Partial<UpdateValues> = {
        name: v.name, status: v.status, roles: v.roles,
        dept_scope: v.roles.includes(SCOPED_ROLE) ? v.dept_scope : [],
      };
      if (v.password) body.password = v.password;
      return api.patch(`/admin/users/${user!.id}`, body);
    },
    onSuccess: () => {
      toast.success("User updated");
      qc.invalidateQueries({ queryKey: ["users"] });
      onOpenChange(false);
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Update failed"),
  });

  const roles = rolesData?.items ?? [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{isEdit ? `Edit ${user?.name}` : "New user"}</DialogTitle>
          <DialogDescription>{isEdit ? "Roles control which sections they can edit." : "Send the password securely; the user can change it after first login."}</DialogDescription>
        </DialogHeader>
        {isEdit ? (
          <form
            className="space-y-3"
            onSubmit={updateForm.handleSubmit(
              (v) => updateMut.mutate(v),
              () => toast.error("Please fix the highlighted fields before saving."),
            )}
          >
            <div className="space-y-1.5">
              <Label htmlFor="u-name">Name</Label>
              <Input id="u-name" {...updateForm.register("name")} />
              {updateForm.formState.errors.name && <p className="text-xs text-destructive">{updateForm.formState.errors.name.message}</p>}
            </div>
            <div className="space-y-1.5">
              <Label>Status</Label>
              <Select value={updateForm.watch("status")} onValueChange={(v) => updateForm.setValue("status", v as "active" | "suspended" | "invited")}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="suspended">Suspended</SelectItem>
                  <SelectItem value="invited">Invited</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <RoleCheckboxes
              roles={roles}
              value={updateForm.watch("roles")}
              onChange={(v) => updateForm.setValue("roles", v, { shouldValidate: true })}
            />
            {updateForm.watch("roles").includes(SCOPED_ROLE) && (
              <DeptScopePicker
                value={updateForm.watch("dept_scope")}
                onChange={(v) => updateForm.setValue("dept_scope", v, { shouldValidate: true })}
                error={updateForm.formState.errors.dept_scope?.message}
              />
            )}
            <div className="space-y-1.5">
              <Label htmlFor="u-pw">New password (leave blank to keep current)</Label>
              <Input id="u-pw" type="password" placeholder="••••••••" {...updateForm.register("password")} />
              {updateForm.formState.errors.password && <p className="text-xs text-destructive">{updateForm.formState.errors.password.message}</p>}
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button type="submit" disabled={updateMut.isPending}>{updateMut.isPending ? "Saving…" : "Save"}</Button>
            </DialogFooter>
          </form>
        ) : (
          <form className="space-y-3" onSubmit={createForm.handleSubmit((v) => createMut.mutate(v))}>
            <div className="space-y-1.5">
              <Label htmlFor="c-email">Email</Label>
              <Input id="c-email" type="email" autoComplete="off" {...createForm.register("email")} />
              {createForm.formState.errors.email && <p className="text-xs text-destructive">{createForm.formState.errors.email.message}</p>}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="c-name">Name</Label>
              <Input id="c-name" {...createForm.register("name")} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="c-pw">Password</Label>
              <Input id="c-pw" type="password" autoComplete="new-password" {...createForm.register("password")} />
              {createForm.formState.errors.password && <p className="text-xs text-destructive">{createForm.formState.errors.password.message}</p>}
            </div>
            <RoleCheckboxes
              roles={roles}
              value={createForm.watch("roles")}
              onChange={(v) => createForm.setValue("roles", v, { shouldValidate: true })}
            />
            {createForm.formState.errors.roles && <p className="text-xs text-destructive">{createForm.formState.errors.roles.message}</p>}
            {createForm.watch("roles").includes(SCOPED_ROLE) && (
              <DeptScopePicker
                value={createForm.watch("dept_scope")}
                onChange={(v) => createForm.setValue("dept_scope", v, { shouldValidate: true })}
                error={createForm.formState.errors.dept_scope?.message}
              />
            )}
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button type="submit" disabled={createMut.isPending}>{createMut.isPending ? "Creating…" : "Create user"}</Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

function RoleCheckboxes({
  roles, value, onChange,
}: {
  roles: { slug: string; name: string }[];
  value: string[];
  onChange: (v: string[]) => void;
}) {
  return (
    <div className="space-y-1.5">
      <Label>Roles</Label>
      <div className="grid grid-cols-2 gap-1.5">
        {roles.map((r) => {
          const checked = value.includes(r.slug);
          return (
            <label key={r.slug} className="flex cursor-pointer items-center gap-2 rounded-md border px-3 py-2 text-sm hover:bg-secondary">
              <input
                type="checkbox"
                className="h-3.5 w-3.5"
                checked={checked}
                onChange={(e) => {
                  if (e.target.checked) onChange([...value, r.slug]);
                  else onChange(value.filter((x) => x !== r.slug));
                }}
              />
              {r.name}
              <span className="ml-auto text-xs text-muted-foreground">{r.slug}</span>
            </label>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Departments a Department Head may manage. Stored on their department_head
 * role assignment as scope_json {"dept":[…]}; the API enforces it on every
 * department-keyed table. Changing it signs the user out of other sessions so
 * the new scope applies on their next login.
 */
function DeptScopePicker({
  value, onChange, error,
}: {
  value: string[];
  onChange: (v: string[]) => void;
  error?: string;
}) {
  const { all, isLoading, isError } = useDepartments({ ignoreScope: true });
  return (
    <div className="space-y-1.5 rounded-md border border-dashed p-3">
      <Label>Department scope</Label>
      <p className="text-xs text-muted-foreground">
        A Department Head can only see and edit content for the departments ticked here.
      </p>
      {isLoading ? (
        <p className="text-xs text-muted-foreground">Loading departments…</p>
      ) : isError ? (
        <p className="text-xs text-destructive">Could not load departments.</p>
      ) : (
        <div className="grid max-h-48 grid-cols-2 gap-1 overflow-y-auto">
          {all.map((d) => {
            const checked = value.includes(d.slug);
            return (
              <label key={d.slug} className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-sm hover:bg-secondary">
                <input
                  type="checkbox"
                  className="h-3.5 w-3.5"
                  checked={checked}
                  onChange={(e) => onChange(e.target.checked ? [...value, d.slug] : value.filter((x) => x !== d.slug))}
                />
                <span className="truncate">{d.short ?? d.name}</span>
                <span className="ml-auto font-mono text-[10px] text-muted-foreground">{d.slug}</span>
              </label>
            );
          })}
        </div>
      )}
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}
